import { useEffect, useState } from 'react';
import {
  type Room,
  type Participant,
  type RemoteParticipant,
  type TrackPublication,
  type TranscriptionSegment,
  RoomEvent,
  Track,
} from 'livekit-client';
import {
  emptyState,
  applySegments,
  applyMicState,
  applySpeakingState,
  rowToArray,
  type CaptionState,
  type IncomingSegment,
  type LocalCaption,
} from '../lib/captions.js';
import { isUserRole } from '@aslgo/shared';

export interface UseCaptionsResult {
  learner: LocalCaption[];
  guide: LocalCaption[];
  speaking: { learner: boolean; guide: boolean };
  micOff: { learner: boolean; guide: boolean };
}

function isCaptioner(p: Participant): boolean {
  return p.attributes?.type === 'captioner' || p.identity.startsWith('captioner-');
}

function toIncoming(segments: TranscriptionSegment[]): IncomingSegment[] {
  return segments.map((s) => ({
    id: s.id,
    text: s.text,
    final: s.final,
    language: s.language,
  }));
}

export function useCaptions(room: Room | null): UseCaptionsResult {
  const [state, setState] = useState<CaptionState>(() => emptyState());

  useEffect(() => {
    if (!room) {
      setState(emptyState());
      return;
    }

    const onTranscription = (
      segments: TranscriptionSegment[],
      participant?: Participant,
      _pub?: TrackPublication,
    ): void => {
      if (!participant || isCaptioner(participant)) return;
      const role = participant.attributes?.role;
      if (!isUserRole(role)) return;
      setState((s) =>
        applySegments(s, toIncoming(segments), { identity: participant.identity, role }, Date.now()),
      );
    };

    const onTrackMuted = (pub: TrackPublication, participant: Participant): void => {
      if (pub.source !== Track.Source.Microphone) return;
      const role = participant.attributes?.role;
      if (!isUserRole(role)) return;
      setState((s) => applyMicState(s, role, false));
    };

    const onTrackUnmuted = (pub: TrackPublication, participant: Participant): void => {
      if (pub.source !== Track.Source.Microphone) return;
      const role = participant.attributes?.role;
      if (!isUserRole(role)) return;
      setState((s) => applyMicState(s, role, true));
    };

    const onActiveSpeakersChanged = (speakers: Participant[]): void => {
      const active = new Set(
        speakers
          .map((p) => p.attributes?.role)
          .filter((r): r is 'learner' | 'guide' => isUserRole(r)),
      );
      setState((s) => {
        let next = s;
        next = applySpeakingState(next, 'learner', active.has('learner'));
        next = applySpeakingState(next, 'guide', active.has('guide'));
        return next;
      });
    };

    room.on(RoomEvent.TranscriptionReceived, onTranscription);
    room.on(RoomEvent.TrackMuted, onTrackMuted);
    room.on(RoomEvent.TrackUnmuted, onTrackUnmuted);
    room.on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);

    // Seed initial mic state for participants already in the room.
    setState((s) => {
      let next = s;
      for (const participant of room.remoteParticipants.values()) {
        const role = (participant as RemoteParticipant).attributes?.role;
        if (!isUserRole(role)) continue;
        for (const pub of (participant as RemoteParticipant).audioTrackPublications.values()) {
          if (pub.source === Track.Source.Microphone) {
            next = applyMicState(next, role, !pub.isMuted);
          }
        }
      }
      return next;
    });

    return () => {
      room.off(RoomEvent.TranscriptionReceived, onTranscription);
      room.off(RoomEvent.TrackMuted, onTrackMuted);
      room.off(RoomEvent.TrackUnmuted, onTrackUnmuted);
      room.off(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
    };
  }, [room]);

  return {
    learner: rowToArray(state.buffers.learner),
    guide: rowToArray(state.buffers.guide),
    speaking: state.speaking,
    micOff: state.micOff,
  };
}
